import { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { useCurrentUser } from '@/api/hooks';

const DISMISS_KEY = 'alfie:install-banner-dismissed-at';
const DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7d — installs are aspirational, don't nag daily

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
  prompt(): Promise<void>;
}

function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  // iOS Safari uses navigator.standalone
  return Boolean((window.navigator as Navigator & { standalone?: boolean }).standalone);
}

function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  // iPad on iOS 13+ reports as Mac — also detect via touch points.
  const isIpadOs =
    ua.includes('Macintosh') &&
    'ontouchend' in document &&
    (navigator.maxTouchPoints ?? 0) > 1;
  return /iPad|iPhone|iPod/.test(ua) || isIpadOs;
}

/**
 * PWA install prompt.
 *
 * - Android Chrome / desktop Chrome / Edge: captures `beforeinstallprompt`,
 *   shows an "Install Alfie" button, calls e.prompt() on click.
 * - iOS Safari (no beforeinstallprompt API): shows static "Share → Add to
 *   Home Screen" instructions, only when actually on iOS Safari and not
 *   already running in standalone mode.
 *
 * Hidden when:
 *   - already running standalone (already installed)
 *   - `appinstalled` event fires
 *   - user dismissed within the last 7 days
 *   - browser doesn't support installs and isn't iOS Safari (nothing actionable)
 */
export function InstallPwaBanner() {
  const { data: user } = useCurrentUser();
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState<boolean>(() => isStandalone());
  const [snoozedUntil, setSnoozedUntil] = useState<number>(() => {
    if (typeof localStorage === 'undefined') return 0;
    return Number(localStorage.getItem(DISMISS_KEY) ?? 0);
  });

  useEffect(() => {
    function onBeforeInstall(e: Event) {
      e.preventDefault();
      setInstallEvent(e as BeforeInstallPromptEvent);
    }
    function onAppInstalled() {
      setInstalled(true);
      setInstallEvent(null);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  if (!user) return null;
  if (installed) return null;
  if (Date.now() < snoozedUntil) return null;

  const ios = isIos();
  // If neither Android-style prompt nor iOS Safari, there's nothing actionable.
  if (!installEvent && !ios) return null;

  function handleDismiss() {
    const until = Date.now() + DISMISS_TTL_MS;
    localStorage.setItem(DISMISS_KEY, String(until));
    setSnoozedUntil(until);
  }

  async function handleInstall() {
    if (!installEvent) return;
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    if (choice.outcome === 'accepted') {
      // appinstalled handler will flip `installed`; clear the captured event.
      setInstallEvent(null);
    } else {
      handleDismiss();
    }
  }

  if (installEvent) {
    return (
      <div className="mb-3 flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm">
        <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <p className="font-medium text-foreground">Install Alfie</p>
          <p className="text-xs text-muted-foreground">
            Get push notifications and a faster experience. One tap to install.
          </p>
        </div>
        <button
          type="button"
          onClick={handleInstall}
          className="shrink-0 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
        >
          Install
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Not now"
          className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  // iOS Safari path — static instructions, no programmatic prompt available.
  return (
    <div className="mb-3 flex items-center gap-3 rounded-xl border border-border bg-card px-4 py-3 text-sm">
      <Share className="h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="flex-1 min-w-0">
        <p className="font-medium text-foreground">Install Alfie on your iPhone</p>
        <p className="text-xs text-muted-foreground">
          Tap <span className="font-medium">Share</span> in Safari, then{' '}
          <span className="font-medium">Add to Home Screen</span> to get push notifications.
        </p>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Not now"
        className="shrink-0 rounded-lg p-1.5 text-muted-foreground hover:bg-secondary hover:text-foreground"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
